import React, { forwardRef } from "react";
import { Card, OverlayTrigger, Popover } from "react-bootstrap";
import errorImage from "../../../../assets/images/miscellaneous_package.png";
import {
	getInteractionsList,
	getPrimaryUseList,
} from "../../../PharmacyMedicineAdd/utils/itemListFields";
import ProductMetaBadges from "../../../PharmacyMedicineAdd/components/ProductMetaBadges";
import { getPackagingDetail, getCardSizeLabel } from "../../../PharmacyMedicineAdd/utils/productMetaFields";

export const getMedicineComposition = (medicine) =>
	medicine.salt_composition_new || medicine.salt_composition || "";

const formatListField = (values) => {
	if (!values?.length) return "";
	return values.join(", ");
};

/** Same markup as research grid — use with OverlayTrigger + transition={false} */
export const buildMedicineResearchPopover = (medicine, t) => {
	const composition = getMedicineComposition(medicine);
	const primaryUse = formatListField(getPrimaryUseList(medicine));
	const interactions = formatListField(getInteractionsList(medicine));
	const packagingDetail = getPackagingDetail(medicine);

	return (
		<Popover id={`pos-medicine-research-${medicine._id}`} className="pos-item-grid-popover">
			<Popover.Header as="h3">{medicine.name}</Popover.Header>
			<Popover.Body>
				{composition && (
					<div className="pos-item-grid-popover-detail">
						<span className="pos-item-grid-popover-label">{t("Composition")}</span>
						<span>{composition}</span>
					</div>
				)}
				{medicine.manufacturer && (
					<div className="pos-item-grid-popover-detail">
						<span className="pos-item-grid-popover-label">{t("Manufacturer")}</span>
						<span>{medicine.manufacturer}</span>
					</div>
				)}
				{primaryUse && (
					<div className="pos-item-grid-popover-detail">
						<span className="pos-item-grid-popover-label">{t("Primary Use")}</span>
						<span>{primaryUse}</span>
					</div>
				)}
				{interactions && (
					<div className="pos-item-grid-popover-detail">
						<span className="pos-item-grid-popover-label">{t("Interactions")}</span>
						<span>{interactions}</span>
					</div>
				)}
				<div className="pos-item-grid-popover-detail">
					<span className="pos-item-grid-popover-label">{t("Stock")}</span>
					<span className="pos-item-grid-popover-stock is-out">{t("Out of Stock")}</span>
				</div>
				{packagingDetail ? (
					<ProductMetaBadges
						packagingDetail={packagingDetail}
						className="pos-popover-meta"
					/>
				) : null}
			</Popover.Body>
		</Popover>
	);
};

export const ProductPopover = forwardRef(
	({ id, title, meta, fields, className, ...rest }, ref) => {
		const { className: injectedClass, ...popoverProps } = rest;
		const mergedClass = ["pos-item-grid-popover", className, injectedClass]
			.filter(Boolean)
			.join(" ");

		return (
			<Popover ref={ref} {...popoverProps} id={id} className={mergedClass}>
				<Popover.Header as="h3">{title}</Popover.Header>
				<Popover.Body>
					{meta}
					{fields.map(
						(field) =>
							field.value != null &&
							field.value !== "" && (
								<div key={field.label} className="pos-item-grid-popover-detail">
									<span className="pos-item-grid-popover-label">{field.label}</span>
									<span className={field.className || ""}>{field.value}</span>
								</div>
							)
					)}
				</Popover.Body>
			</Popover>
		);
	}
);

ProductPopover.displayName = "ProductPopover";

const PosItemCard = ({
	icon,
	title,
	subtitle,
	stockQty,
	meta,
	isOutOfStock = false,
	isResearch = false,
	disabled = false,
	onClick,
	popover,
}) => {
	const stockUnit = meta?.stockUnitLabel || "";
	const packageQtyLine = meta?.packageQtyLine || "";
	const cardSizeLabel = getCardSizeLabel(meta?.sizeLabel, meta?.quantity);
	const showPkgChip = Boolean(packageQtyLine || cardSizeLabel);

	const card = (
		<Card
			className={`pos-item-grid-card pos-item-grid-card--compact w-100 ${
				isOutOfStock || disabled ? "disabled-card" : ""
			} ${isResearch ? "pos-item-research-card" : ""}`}
			onClick={onClick}
			style={{
				cursor: isOutOfStock || disabled || isResearch ? "default" : "pointer",
				opacity: isOutOfStock ? 0.55 : 1,
			}}
		>
			<div className="pos-item-grid-card-media">
				<div
					className={`pos-item-grid-stock-badge ${
						isOutOfStock || (isResearch && Number(stockQty) === 0) ? "is-zero" : ""
					}`}
				>
					<span className="pos-item-grid-stock-qty">
						{isOutOfStock ? "0" : stockQty}
					</span>
					{stockUnit ? (
						<span className="pos-item-grid-stock-unit">{stockUnit}</span>
					) : null}
				</div>
				{showPkgChip ? (
					<div
						className="pos-item-grid-card-pkg-chip"
						title={[packageQtyLine, cardSizeLabel].filter(Boolean).join("\n")}
					>
						{packageQtyLine ? (
							<span className="pos-item-grid-card-pkg-line pos-item-grid-card-pkg-text">
								{packageQtyLine}
							</span>
						) : null}
						{cardSizeLabel ? (
							<span className="pos-item-grid-card-pkg-line pos-item-grid-card-pkg-size">
								{cardSizeLabel}
							</span>
						) : null}
					</div>
				) : null}
				<div className="pos-box-img pos-box-img--compact">
					<img
						src={icon}
						onError={(event) => {
							event.target.src = errorImage;
						}}
						alt={title}
					/>
				</div>
			</div>
			<Card.Body className="pos-item-grid-card-body pos-item-grid-card-body--compact">
				<Card.Title className="pos-item-grid-card-title" title={title}>
					{title}
				</Card.Title>
				{subtitle && (
					<small className="pos-item-grid-card-subtitle" title={subtitle}>
						{subtitle}
					</small>
				)}
			</Card.Body>
		</Card>
	);

	if (!popover) {
		return <div className="pos-item-grid-card-trigger">{card}</div>;
	}

	return (
		<OverlayTrigger
			trigger={["hover", "focus"]}
			placement="auto"
			delay={{ show: 200, hide: 100 }}
			transition={false}
			popperConfig={{ strategy: "fixed" }}
			overlay={popover}
		>
			<div className="pos-item-grid-card-trigger" tabIndex={0}>
				{card}
			</div>
		</OverlayTrigger>
	);
};

export default PosItemCard;
